/**
 * Firebase App Check initialization for web (reCAPTCHA v3)
 * Initializes App Check once as a singleton to prevent duplicate initialization.
 * Debug mode is enabled automatically for localhost/dev environments.
 * Skips initialization when running in native WebView (iOS/Android).
 */

import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { isNativeWebView } from "../nativeBridge.ts";

// Singleton flag to prevent multiple initializations
let appCheckInitialized = false;

/**
 * Initialize Firebase App Check with reCAPTCHA v3
 * Only initializes once, even if called multiple times
 * @param {FirebaseApp} app - The Firebase app instance
 */
export function initializeFirebaseAppCheck(app) {
  // Guard: only initialize once
  if (appCheckInitialized) {
    console.log("[App Check] Already initialized, skipping");
    return;
  }

  if (!app) {
    console.error("[App Check] Firebase app instance is required");
    return;
  }

  // Skip App Check in native WebViews (reCAPTCHA doesn't work reliably in WebViews)
  if (isNativeWebView()) {
    console.log("[App Check] Skipping initialization in native WebView");
    return;
  }

  // Get site key from environment variable (CRA uses REACT_APP_* prefix)
  const siteKey = process.env.REACT_APP_FIREBASE_APPCHECK_SITE_KEY;

  if (!siteKey) {
    console.warn(
      "[App Check] Site key not found. Please set REACT_APP_FIREBASE_APPCHECK_SITE_KEY in .env.local"
    );
    return;
  }

  // Enable debug mode for localhost/dev environments
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";
  const isDevelopment = process.env.NODE_ENV !== "production";

  if (isLocalhost && isDevelopment) {
    // Set debug token BEFORE initializeAppCheck
    // This allows Firebase to generate a debug token for localhost
    if (typeof self !== "undefined") {
      self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }
    console.log("[App Check] Debug mode enabled for localhost");
  }

  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });

    appCheckInitialized = true;

    if (isLocalhost && isDevelopment) {
      // After initialization, Firebase will log the debug token to console
      // Wait a moment then show helpful message
      setTimeout(() => {
        console.log(
          "%c[App Check] Debug Token Generated",
          "color: #4CAF50; font-weight: bold; font-size: 14px;"
        );
        console.log(
          "%c⚠️ IMPORTANT: Copy the debug token from the console above (look for 'Firebase App Check debug token:')",
          "color: #FF9800; font-weight: bold;"
        );
        console.log(
          "%cThen paste it into Firebase Console → App Check → Manage debug tokens",
          "color: #2196F3;"
        );
        console.log(
          "Full path: https://console.firebase.google.com/project/YOUR_PROJECT_ID/appcheck/apps"
        );
      }, 1000);
    } else {
      console.log("[App Check] Initialized successfully (production mode)");
    }
  } catch (error) {
    console.error("[App Check] Initialization failed:", error);
    // Don't throw - App Check is non-blocking for app functionality
  }
}
